// Las secciones de la ficha de un producto que YA está en Tiendanube arrancan trabadas
// (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas). Una trabada no se
// manda al publicar: queda como en la tienda. Se destraba con "Editar en la tienda", o sola cuando el
// chat cambia un campo suyo. Puro: sin red ni React.
//
// `lanzamientos_productos.secciones_destrabadas` guarda las destrabadas. null = fila de antes (o sin
// la migración): nada está trabado y se manda todo lo que cambió, como siempre.

import type { CamposDeFicha, CategoriaDeTienda } from "./lanzamientosApi";
import type { ProductoEnLaTienda } from "./cambiosContraLaTienda";
import { camposDesdeLaTienda, GENEROS, RANGOS_DE_EDAD, VISIBILIDADES } from "./camposDeLaTienda";

/** Las secciones de la ficha que se pueden trabar (los `id` de `Seccion` en FichaDeProducto). Fotos no: siempre se manda. */
export type SeccionTrabable =
  | "basico"
  | "precio"
  | "inventario"
  | "envio"
  | "variantes"
  | "categorias"
  | "seo"
  | "google"
  | "visibilidad"
  | "envioGratis";

export const SECCIONES_TRABABLES: readonly SeccionTrabable[] = [
  "basico",
  "precio",
  "inventario",
  "envio",
  "variantes",
  "categorias",
  "seo",
  "google",
  "visibilidad",
  "envioGratis",
];

/** El título de cada sección, como en la ficha. */
export const TITULO_DE_SECCION: Record<SeccionTrabable, string> = {
  basico: "Lo básico",
  precio: "Precio y costo",
  inventario: "Inventario",
  envio: "Envío",
  variantes: "Variantes",
  categorias: "Categorías",
  seo: "Marca, etiquetas y SEO",
  google: "Instagram y Google Shopping",
  visibilidad: "Visibilidad",
  envioGratis: "Envío gratis",
};

/** ¿Esta sección está trabada? Sin lista (null), ninguna lo está. */
export function estaTrabada(destrabadas: readonly string[] | null | undefined, seccion: string): boolean {
  if (destrabadas == null) return false;
  if (!(SECCIONES_TRABABLES as readonly string[]).includes(seccion)) return false;
  return !destrabadas.includes(seccion);
}

const SECCION_DE_CAMPO: Partial<Record<keyof CamposDeFicha, SeccionTrabable>> = {
  nombre: "basico",
  descripcion: "basico",
  precio: "precio",
  costo: "precio",
  stock: "inventario",
  sku: "inventario",
  codigoBarras: "inventario",
  peso: "envio",
  alto: "envio",
  ancho: "envio",
  profundidad: "envio",
  marca: "seo",
  tags: "seo",
  seoTitulo: "seo",
  seoDescripcion: "seo",
  mpn: "google",
  rangoEdad: "google",
  genero: "google",
  visibilidad: "visibilidad",
  envioGratis: "envioGratis",
};

/** La sección de un campo de la ficha. null = no es de una sección trabable (la acción de las fotos, los interruptores). */
export function seccionDeCampo(campo: string): SeccionTrabable | null {
  return SECCION_DE_CAMPO[campo as keyof CamposDeFicha] ?? null;
}

/** La lista con estas secciones destrabadas, sin repetir y en el orden de la ficha. */
export function conDestrabadas(destrabadas: readonly string[] | null | undefined, sumar: readonly string[]): string[] {
  const todas = new Set([...(destrabadas ?? []), ...sumar]);
  return SECCIONES_TRABABLES.filter((s) => todas.has(s));
}

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
/** Como lo escribe la ficha: sin ceros de más ("1.2", no "1.20"). */
const numero = (n: number | null) => (n === null ? null : String(Number(n.toFixed(2))));

/**
 * Lo que hoy está publicado en una sección, en una línea: el resumen de una sección trabada. Sale de la
 * tienda y no de la ficha, porque una trabada puede guardar cambios que no se mandan.
 */
export function resumenEnLaTienda(
  seccion: SeccionTrabable,
  e: ProductoEnLaTienda,
  arbol: readonly CategoriaDeTienda[],
): string {
  const vs = e.variantes;
  const unaSola = vs.length === 1 ? vs[0]! : null;
  switch (seccion) {
    case "basico":
      return e.nombre || "Sin nombre";
    case "precio": {
      const precios = vs.map((v) => v.precio).filter((p): p is number => p !== null);
      if (precios.length === 0) return "Sin precio";
      const min = Math.min(...precios);
      const max = Math.max(...precios);
      return min === max ? `$${numero(min)}` : `$${numero(min)} a $${numero(max)}`;
    }
    case "inventario": {
      if (unaSola) {
        const stock = unaSola.stock === null ? "Stock sin límite" : `${unaSola.stock} en stock`;
        return [unaSola.sku, stock].filter(Boolean).join(" · ");
      }
      if (vs.some((v) => v.stock === null)) return "Stock sin límite";
      return `${vs.reduce((n, v) => n + (v.stock ?? 0), 0)} en stock`;
    }
    case "envio": {
      const v = unaSola ?? vs[0];
      if (!v || (v.peso === null && v.alto === null)) return "Sin peso ni medidas";
      const peso = v.peso !== null ? `${numero(v.peso)} kg` : null;
      const medidas = v.alto !== null ? `${numero(v.alto)}×${numero(v.ancho) ?? "?"}×${numero(v.profundidad) ?? "?"} cm` : null;
      return [peso, medidas].filter(Boolean).join(" · ");
    }
    case "variantes":
      return vs.length > 1 ? `${e.atributos.join(" · ")} — ${plural(vs.length, "variante", "variantes")}` : "Producto sin variantes";
    case "categorias": {
      const nombreDe = new Map(arbol.map((c) => [c.tnCategoryId, c.nombre]));
      const nombres = e.categorias.flatMap((id) => (nombreDe.has(id) ? [nombreDe.get(id)!] : []));
      if (e.categorias.length === 0) return "Sin categoría";
      return nombres.length > 0 ? nombres.join(", ") : plural(e.categorias.length, "categoría", "categorías");
    }
    case "seo":
      return [e.marca, e.tags, e.seo_titulo].filter(Boolean).join(" · ") || "Sin completar";
    case "google": {
      const c = camposDesdeLaTienda(e);
      return (
        [c.mpn, RANGOS_DE_EDAD.find((x) => x.id === c.rangoEdad)?.texto, GENEROS.find((x) => x.id === c.genero)?.texto]
          .filter(Boolean)
          .join(" · ") || "Sin completar"
      );
    }
    case "visibilidad":
      return VISIBILIDADES.find((x) => x.id === camposDesdeLaTienda(e).visibilidad)?.texto ?? "Visible";
    case "envioGratis":
      return camposDesdeLaTienda(e).envioGratis ? "Con envío gratis" : "Sin envío gratis";
  }
}
